import { ViewportScroller } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventAdminControllerService, EventInfoRequest, EventInfoResponse } from '@ent-regis/entregis-ts-angular';
import { faInfoCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { appConst } from 'src/app/constants/app-const';
import { FormUtilsService } from '../../services/form-utils.service';
import { SubEventInfoResponse } from '@ent-regis/entregis-ts-angular/model/subEventInfoResponse';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { EventService } from 'src/app/layout/event/event.service';

@Component({
  selector: 'app-event-form-info',
  templateUrl: './event-form-info.component.html',
  styleUrls: ['./event-form-info.component.scss'],
  providers: [
    EventService
  ]
})
export class EventFormInfoComponent implements OnInit, OnChanges {
  
  form: FormGroup;
  get f() {
    return this.form.controls;
  }
  eventTypeEnum = EventInfoResponse.EventTypeEnum;
  public faInfoCircle = faInfoCircle;
  public faTimes = faTimesCircle;

  isShowHotel = true;
  isShowSalePersonForInvestorRegistration = true;
  isShowSendScheduleEmail = false;

  isSubmitted = false;
  isDeleteLogo = false;
  isDeleteBanner = false;
  isDeleteBackground = false;

  companyColor: string = null;
  investorColor: string = null;
  seminarInvestorColor: string = null;
  cpPresetColors = ['#fff', '#000', '#C0C0C0', '#0000FF', '#8A2BE2', '#2889e9', '#5DADE2', '#008000', '#52BE80', '#fff500', '#e920e9', '#FF0000', '#EC7063'];

  private isFirstLoad = true; // Flag to track first execution
  subEventList: SubEventInfoResponse[] = [];

  previousDateRange: any;

  @ViewChild("confirmModal", { static: false })
  confirmModal: ConfirmModalComponent;

  @Input()
  eventInfo: EventInfoResponse;
  @Output() saveSuccess = new EventEmitter<{ isSuccess: boolean, eventAlias: string }>();

  constructor(
    private eventAdminService: EventAdminControllerService,
    private viewPortScroller: ViewportScroller,
    private formUtils: FormUtilsService,
    private eventService: EventService
  ) { }

  ngOnChanges(): void {
    if (!this.eventInfo) return;

    this.applyEventFlags();

    if (!this.form) return;

    this.patchFormValues();

    if (this.eventInfo.eventType === this.eventTypeEnum.DIGITALROADSHOW) {
      this.handleDigitalRoadshow();
    }
  }

  ngOnInit() {
    this.createForm();

    this.form.get('dateRange').valueChanges.subscribe(dateRange => {
      if (this.isFirstLoad && dateRange) {
        this.previousDateRange = dateRange;
      }
      if (!this.isFirstLoad && (this.eventInfo && (this.eventInfo.subEvent || this.eventInfo.isSubEvent))) {
        this.confirmDateChange(dateRange);

      } else if (!this.isFirstLoad) {
         if (this.form.get('subEventId')) {
          this.form.get('subEventId').setValue(null);
        }
        if (dateRange && dateRange.length === 2) {
          const startDate = dateRange[0];
          const endDate = dateRange[1];
          this.listSubEvent(startDate, endDate);
        }

      }  else {
        this.listSubEvent();
      }

      this.isFirstLoad = false; // Set flag to false after first execution
    });
  }

  private applyEventFlags(): void {
    const e = this.eventInfo;

    this.isShowHotel = e.isShowHotel || false;
    this.isShowSalePersonForInvestorRegistration = e.isShowSalePersonForInvestorRegistration || false;
    this.isShowSendScheduleEmail = e.isSendScheduleEmail || false;
    this.companyColor = e.companyColor || null;
    this.investorColor = e.investorColor || null;
    this.seminarInvestorColor = e.seminarInvestorColor || null;
  }

  private patchFormValues(): void {
    const e = this.eventInfo;

    this.form.patchValue({
      eventLogoUrl: e.eventLogoUrl,
      eventLogoBase64: null,
      eventLogoFileName: null,
      eventName: e.eventName,
      eventType: e.eventType,
      bannerUrl: e.bannerUrl,
      prefixTopicWebex: e.prefixTopicWebex,
      bannerBase64: null,
      bannerFileName: null,
      eventAlias: e.eventAlias,
      dateRange: [new Date(e.startDate), new Date(e.endDate)],
      dateRangeRegisterCompany: [new Date(e.startDateRegisterCompany), new Date(e.endDateRegisterCompany)],
      dateRangeRegisterInvestor: [new Date(e.startDateRegisterInvestor), new Date(e.endDateRegisterInvestor)],
      dateRangeRegisterSeminarInvestor: [new Date(e.startDateRegisterSeminarInvestor),  new Date(e.endDateRegisterSeminarInvestor)],
      reminder: e.reminderText,
      location: e.location,
      participantLimit: e.participantLimit || 5,
      backgroundUrl: e.bgUrl,
      backgroundBase64: null,
      backgroundFileName: null,
      subEventId: e.subEvent ? e.subEvent.eventId : null,
    });
  }

  private handleDigitalRoadshow(): void {
    this.form.controls.eventType.disable();
    this.form.controls.dateRange.disable();
    this.form.controls.dateRangeRegisterCompany.disable();
    this.form.updateValueAndValidity();
  }

  confirmDateChange(dateRange: any) {
    // Temporarily revert back to previous value (optional visual UX)
    if (this.previousDateRange) {
      this.form.get('dateRange').setValue(this.previousDateRange, { emitEvent: false });
    }
    
    this.confirmModal.title = 'Confirm Event Period Change';
    this.confirmModal.message = `Are you sure you want to change the Event Period? This action will affect the Sub/Main Event.`;
    this.confirmModal.btnConfirm = 'Confirm';
    this.confirmModal.class = 'modal-lg';
    this.confirmModal.openModal();

    this.confirmModal.confirm = () => {
      if (this.form.get('subEventId')) {
          this.form.get('subEventId').setValue(null);
        }
      if (dateRange && dateRange.length === 2) {
        const startDate = dateRange[0];
        const endDate = dateRange[1];
        this.listSubEvent(startDate, endDate);
      }
      this.previousDateRange = dateRange;
      this.form.get('dateRange').setValue(dateRange, { emitEvent: false });
      this.confirmModal.onClose();
    };
  }

  createForm(): void {
    const today = new Date();
    this.form = new FormGroup({
      eventLogoBase64: new FormControl(null),
      eventLogoFileName: new FormControl(null),
      eventLogoUrl: new FormControl(null),
      eventName: new FormControl('', [Validators.required, Validators.maxLength(255)]),
      eventType: new FormControl(null, Validators.required),
      bannerBase64: new FormControl(null),
      bannerFileName: new FormControl(null),
      bannerUrl: new FormControl(null),
      backgroundBase64: new FormControl(null),
      backgroundFileName: new FormControl(null),
      backgroundUrl: new FormControl(null),
      eventAlias: new FormControl('', [Validators.required, Validators.maxLength(255), Validators.pattern(appConst.aliasPattern)]),
      dateRange: new FormControl(null, Validators.required),
      dateRangeRegisterCompany: new FormControl(null, Validators.required),
      dateRangeRegisterInvestor: new FormControl(null, Validators.required),
      dateRangeRegisterSeminarInvestor: new FormControl(null, Validators.required),
      location: new FormControl(null),
      reminder: new FormControl(null),
      prefixTopicWebex: new FormControl(`Thailand Focus ${today.getFullYear()}`, Validators.required),
      participantLimit: new FormControl(5, Validators.required),
      subEventId: new FormControl(null)
    });
  }

  handleUpload(event: any, id: string): void {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (id === 'eventLogo') {
        this.form.controls.eventLogoBase64.setValue(reader.result);
        this.form.controls.eventLogoFileName.setValue(file.name);
      } else if (id === 'banner') {
        this.form.controls.bannerBase64.setValue(reader.result);
        this.form.controls.bannerFileName.setValue(file.name);
      } else {
        this.form.controls.backgroundBase64.setValue(reader.result);
        this.form.controls.backgroundFileName.setValue(file.name);
      }
      this.form.updateValueAndValidity();
    };
  }

  showSubEvent(): boolean {
    return this.eventInfo && this.eventInfo.eventAlias && this.eventInfo.eventType === this.eventTypeEnum.THAILANDFOCUS;
  }

  listSubEvent(startDate?: Date, enyDate?: Date): void {
    if (this.showSubEvent()) {
      this.eventAdminService.getSubEventAvailableUsingGET(this.eventInfo.eventAlias, enyDate, startDate).subscribe((result) => {
        this.subEventList = result.data;
      });
    }
  }

  deleteImage(id: string): void {
    if (id === 'eventLogo') {
      (<HTMLInputElement>document.getElementById('event-form-info-field-event-logo-input')).value = '';
      this.form.controls.eventLogoUrl.setValue(null);
      this.form.controls.eventLogoBase64.setValue(null);
      this.form.controls.eventLogoFileName.setValue(null);
      this.isDeleteLogo = true;
    } else if (id === 'banner') {
      (<HTMLInputElement>document.getElementById('event-form-info-field-banner-input')).value = '';
      this.form.controls.bannerUrl.setValue(null);
      this.form.controls.bannerBase64.setValue(null);
      this.form.controls.bannerFileName.setValue(null);
      this.isDeleteBanner = true;
    } else {
      (<HTMLInputElement>document.getElementById('event-form-info-field-background-input')).value = '';
      this.form.controls.backgroundUrl.setValue(null);
      this.form.controls.backgroundBase64.setValue(null);
      this.form.controls.backgroundFileName.setValue(null);
      this.isDeleteBackground = true;
    }
    this.form.updateValueAndValidity();
  }

  onSave() {
    this.isSubmitted = true;
    if (this.form.valid) {
      this.eventAliasChanged(() => {
        const form = this.form.getRawValue();
        const request: EventInfoRequest = {
          eventName: form.eventName,
          eventLogoBase64: form.eventLogoBase64,
          eventLogoFileName: form.eventLogoFileName,
          bannerBase64: form.bannerBase64,
          bannerFileName: form.bannerFileName,
          bgBase64: form.backgroundBase64,
          bgFileName: form.backgroundFileName,
          eventAlias: form.eventAlias,
          eventType: form.eventType,
          startDate: form.dateRange[0],
          endDate: form.dateRange[1],
          startDateRegisterCompany: form.dateRangeRegisterCompany[0],
          endDateRegisterCompany: form.dateRangeRegisterCompany[1],
          startDateRegisterInvestor: form.dateRangeRegisterInvestor[0],
          endDateRegisterInvestor: form.dateRangeRegisterInvestor[1],
          startDateRegisterSeminarInvestor: form.dateRangeRegisterSeminarInvestor[0],
          endDateRegisterSeminarInvestor: form.dateRangeRegisterSeminarInvestor[1],
          hasUpdateLogo: this.hasUpdateLogo(),
          hasUpdateBanner: this.hasUpdateBanner(),
          hasUpdateBg: this.hasUpdateBg(),
          isShowHotel: this.isShowHotel,
          isShowSalePersonForInvestorRegistration: this.isShowSalePersonForInvestorRegistration,
          reminderText: form.reminder,
          location: form.location,
          companyColor: this.companyColor,
          investorColor: this.investorColor,
          seminarInvestorColor: this.seminarInvestorColor,
          participantLimit: form.participantLimit,
          isSendScheduleEmail: this.isShowSendScheduleEmail,
          prefixTopicWebex : form.prefixTopicWebex,
          subEventId: form.subEventId
        };
        if (this.eventInfo) {
          this.eventAdminService.updateEventInfoUsingPOST(this.eventInfo.eventId, request).subscribe(() => {
            this.saveSuccess.emit({ isSuccess: true, eventAlias: request.eventAlias });
          });
        } else {
          this.eventAdminService.submitEventUsingPOST(request).subscribe(() => {
            this.saveSuccess.emit({ isSuccess: true, eventAlias: null });
          });
        }
      });
    } else {
      this.form.markAllAsTouched();
      this.viewPortScroller.scrollToPosition([0, 0]);
    }
  }

  eventAliasChanged(complete: () => void) {
    if (this.form.controls.eventAlias.dirty) {
      if (this.eventInfo) {
        if (this.form.value.eventAlias != this.eventInfo.eventAlias) {
          this.eventAdminService.checkAliasDuplicateUsingGET(this.form.value.eventAlias).subscribe((res) => {
            if (res.data) {
              this.formUtils.setError(this.form.controls.eventAlias, 'duplicate', true);
              document.getElementById("event-form-info-field-event-alias-text").scrollIntoView();
            } else {
              this.formUtils.removeError(this.form.controls.eventAlias, 'duplicate');
              if (complete) { complete(); }
            }
          });
        }
      } else {
        this.eventAdminService.checkAliasDuplicateUsingGET(this.form.value.eventAlias).subscribe((res) => {
          if (res.data) {
            this.formUtils.setError(this.form.controls.eventAlias, 'duplicate', true);
            document.getElementById("event-form-info-field-event-alias-text").scrollIntoView();
          } else {
            this.formUtils.removeError(this.form.controls.eventAlias, 'duplicate');
            if (complete) { complete(); }
          }
        });
      }
    } else {
      if (complete) { complete(); }
    }
  }

  onClipboard(event: string) {
    const el = document.createElement('textarea');
    el.value = event;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  hasUpdateLogo(): boolean {
    return this.form.value.eventLogoBase64 !== null || (this.isDeleteLogo && this.eventInfo.eventLogoUrl !== null && this.form.value.eventLogoBase64 === null);
  }

  hasUpdateBanner(): boolean {
    return this.form.value.bannerBase64 !== null || (this.isDeleteBanner && this.eventInfo.bannerUrl !== null && this.form.value.bannerBase64 === null);
  }


  hasUpdateBg(): boolean {
    return this.form.value.backgroundBase64 !== null || (this.isDeleteBackground && this.eventInfo.bgUrl !== null && this.form.value.backgroundBase64 === null);
  }

  onChangeHideHotel(value: boolean) {
    this.isShowHotel = value;
  }

  onChangeShowSalePersonForInvestorRegistration(value: boolean) {
    this.isShowSalePersonForInvestorRegistration = value;
  }

  onChangeShowSendScheduleEmail(value: boolean) {
    this.isShowSendScheduleEmail = value;
  }

}
